// 학습 데이터 분석을 위한 구글시트 연동 함수들

/**
 * 구글시트에서 학습로그 데이터를 가져오는 함수
 * @param {string} sheetUrl - 구글시트 URL
 * @returns {Promise<Array>} 학습로그 데이터 배열
 */
export async function fetchLearningData(sheetUrl) {
  try {
    console.log('📊 학습로그 데이터 가져오기 시작:', sheetUrl);
    
    // 구글시트 ID 추출
    const sheetIdMatch = sheetUrl.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
    if (!sheetIdMatch) {
      throw new Error('올바른 구글시트 URL이 아닙니다.');
    }
    const sheetId = sheetIdMatch[1];

    // Apps Script URL (환경변수에서 가져오기)
    const APPS_SCRIPT_URL = process.env.REACT_APP_APPSCRIPT_URL;
    if (!APPS_SCRIPT_URL) {
      throw new Error('Apps Script URL이 설정되지 않았습니다. .env 파일에 REACT_APP_APPSCRIPT_URL을 설정해주세요.');
    }

    // GET 요청으로 학습로그 조회
    const url = `${APPS_SCRIPT_URL}?sheetId=${sheetId}&menu=learning_logs`;

    console.log('📤 Apps Script GET 요청:', { sheetId, menu: 'learning_logs' });

    const response = await fetch(url, {
      method: 'GET'
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Apps Script 오류 응답:', errorText);
      throw new Error(`Apps Script 오류: ${response.status}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || '학습로그 데이터 가져오기에 실패했습니다.');
    }

    console.log('✅ 학습로그 데이터 가져오기 완료:', result.logs?.length || 0, '개 항목');
    
    // Apps Script에서 이미 파싱된 객체 배열을 받아서 추가 파싱 진행
    return parseLearningLogs(result.logs || []);

  } catch (error) {
    console.error('❌ 학습로그 데이터 가져오기 실패:', error);
    throw error;
  }
}

/**
 * Apps Script에서 받은 학습로그 객체 배열을 추가 파싱하는 함수
 * @param {Array} logsArray - Apps Script에서 받은 로그 객체 배열
 * @returns {Array} 파싱된 학습로그 객체 배열
 */
function parseLearningLogs(logsArray) {
  if (!Array.isArray(logsArray)) {
    console.warn('⚠️ 로그 데이터가 배열이 아닙니다');
    return [];
  }

  const parsedData = [];

  logsArray.forEach((entry, index) => {
    try {
      // 부가정보 JSON 파싱
      if (entry['부가정보']) {
        try {
          entry.parsedMetadata = JSON.parse(entry['부가정보']);
        } catch (jsonError) {
          console.warn(`⚠️ ${index+1}번째 항목의 부가정보 JSON 파싱 실패:`, jsonError);
          entry.parsedMetadata = {};
        }
      }

      // 정오답정보 파싱
      if (entry['정오답정보']) {
        entry.parsedScore = parseScoreInfo(entry['정오답정보']);
      }

      // 학습시간 파싱 (초 단위로 변환)
      if (entry['학습시간']) {
        entry.learningTimeInSeconds = parseLearningTime(entry['학습시간']);
      }

      // 접속시간 파싱
      if (entry['접속시간']) {
        entry.parsedAccessTime = parseAccessTime(entry['접속시간']);
      }

      parsedData.push(entry);

    } catch (error) {
      console.warn(`⚠️ ${index+1}번째 항목 파싱 실패:`, error, entry);
    }
  });

  console.log('✅ 학습로그 파싱 완료:', parsedData.length, '개 항목');
  return parsedData;
}

/**
 * CSV 형태의 학습로그 데이터를 파싱하는 함수 (백업용)
 * @param {string} csvData - CSV 형태의 원본 데이터
 * @returns {Array} 파싱된 학습로그 객체 배열
 */
function parseLearningData(csvData) {
  if (!csvData || typeof csvData !== 'string') {
    console.warn('⚠️ CSV 데이터가 비어있거나 올바르지 않습니다');
    return [];
  }

  const lines = csvData.trim().split('\n');
  if (lines.length <= 1) {
    console.warn('⚠️ CSV 데이터에 헤더만 있고 실제 데이터가 없습니다');
    return [];
  }

  // 첫 번째 줄은 헤더
  const headers = lines[0].split(',').map(h => h.trim());
  console.log('📋 CSV 헤더:', headers);

  const parsedData = [];

  for (let i = 1; i < lines.length; i++) {
    try {
      const values = parseCSVLine(lines[i]);
      
      if (values.length !== headers.length) {
        console.warn(`⚠️ ${i+1}번째 줄의 컬럼 수가 맞지 않습니다:`, values.length, 'vs', headers.length);
        continue;
      }

      const entry = {};
      headers.forEach((header, index) => {
        entry[header] = values[index] || '';
      });

      // 부가정보 JSON 파싱
      if (entry['부가정보']) {
        try {
          entry.parsedMetadata = JSON.parse(entry['부가정보']);
        } catch (jsonError) {
          console.warn(`⚠️ ${i+1}번째 줄의 부가정보 JSON 파싱 실패:`, jsonError);
          entry.parsedMetadata = {};
        }
      }

      // 정오답정보 파싱
      if (entry['정오답정보']) {
        entry.parsedScore = parseScoreInfo(entry['정오답정보']);
      }

      // 학습시간 파싱 (초 단위로 변환)
      if (entry['학습시간']) {
        entry.learningTimeInSeconds = parseLearningTime(entry['학습시간']);
      }

      // 접속시간 파싱
      if (entry['접속시간']) {
        entry.parsedAccessTime = parseAccessTime(entry['접속시간']);
      }

      parsedData.push(entry);

    } catch (error) {
      console.warn(`⚠️ ${i+1}번째 줄 파싱 실패:`, error, lines[i]);
    }
  }

  console.log('✅ 학습로그 파싱 완료:', parsedData.length, '개 항목');
  return parsedData;
}

/**
 * CSV 한 줄을 파싱하는 함수 (쉼표가 포함된 필드 처리)
 * @param {string} line - CSV 한 줄
 * @returns {Array} 파싱된 값들의 배열
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  result.push(current.trim());
  return result;
}

/**
 * 정오답정보를 파싱하는 함수
 * @param {string} scoreInfo - "정답:4, 오답:1, 스킵:0" 형태의 문자열
 * @returns {Object} { correct, wrong, skip, total, accuracy }
 */
function parseScoreInfo(scoreInfo) {
  const result = { correct: 0, wrong: 0, skip: 0, total: 0, accuracy: 0 };
  
  try {
    const parts = scoreInfo.split(',');
    
    parts.forEach(part => {
      const trimmed = part.trim();
      if (trimmed.includes('정답:')) {
        result.correct = parseInt(trimmed.split(':')[1]) || 0;
      } else if (trimmed.includes('오답:')) {
        result.wrong = parseInt(trimmed.split(':')[1]) || 0;
      } else if (trimmed.includes('스킵:')) {
        result.skip = parseInt(trimmed.split(':')[1]) || 0;
      }
    });
    
    result.total = result.correct + result.wrong + result.skip;
    result.accuracy = result.total > 0 ? Math.round((result.correct / result.total) * 100) : 0;
    
  } catch (error) {
    console.warn('⚠️ 정오답정보 파싱 실패:', error, scoreInfo);
  }
  
  return result;
}

/**
 * 학습시간을 초 단위로 변환하는 함수
 * @param {string} timeStr - "1분 4초", "0분 26초", "0" 등의 형태
 * @returns {number} 초 단위 시간
 */
function parseLearningTime(timeStr) {
  if (!timeStr || timeStr === '0') return 0;
  
  let totalSeconds = 0;
  
  try {
    // "1분 4초" 형태 파싱
    const minuteMatch = timeStr.match(/(\d+)분/);
    const secondMatch = timeStr.match(/(\d+)초/);
    
    if (minuteMatch) {
      totalSeconds += parseInt(minuteMatch[1]) * 60;
    }
    
    if (secondMatch) {
      totalSeconds += parseInt(secondMatch[1]);
    }
    
    // "0" 또는 숫자만 있는 경우
    if (!minuteMatch && !secondMatch) {
      const numericValue = parseInt(timeStr);
      if (!isNaN(numericValue)) {
        totalSeconds = numericValue;
      }
    }
    
  } catch (error) {
    console.warn('⚠️ 학습시간 파싱 실패:', error, timeStr);
  }
  
  return totalSeconds;
}

/**
 * 접속시간을 Date 객체로 변환하는 함수
 * @param {string} timeStr - "2025. 8. 13. 오후 1:20:38" 형태
 * @returns {Date} Date 객체
 */
function parseAccessTime(timeStr) {
  try {
    if (!timeStr || typeof timeStr !== 'string') {
      console.warn('⚠️ 접속시간이 유효하지 않습니다:', timeStr);
      return new Date();
    }

    console.log('🔍 원본 시간 문자열:', timeStr);

    // "2025. 8. 13. 오후 1:48:39" 형태를 파싱
    const regex = /(\d{4})\. (\d{1,2})\. (\d{1,2})\. (오전|오후) (\d{1,2}):(\d{2}):(\d{2})/;
    const match = timeStr.match(regex);
    
    if (!match) {
      console.warn('⚠️ 날짜 형식 매칭 실패:', timeStr);
      return new Date();
    }

    const [, year, month, day, ampm, hour, minute, second] = match;
    
    // 시간 변환 (12시간 → 24시간 형식)
    let hour24 = parseInt(hour);
    if (ampm === '오후' && hour24 !== 12) {
      hour24 += 12;
    } else if (ampm === '오전' && hour24 === 12) {
      hour24 = 0;
    }
    
    // Date 객체 생성 (월은 0부터 시작하므로 -1)
    const parsedDate = new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      hour24,
      parseInt(minute),
      parseInt(second)
    );
    
    console.log('🔍 파싱 결과:', timeStr, '→', parsedDate.toLocaleString('ko-KR'));
    
    // 유효한 날짜인지 확인
    if (isNaN(parsedDate.getTime())) {
      console.warn('⚠️ 접속시간 파싱 실패 - 유효하지 않은 날짜:', timeStr);
      return new Date();
    }
    
    return parsedDate;
  } catch (error) {
    console.warn('⚠️ 접속시간 파싱 실패:', error, timeStr);
    return new Date();
  }
}

/**
 * 학습 데이터 분석 함수들
 */

/**
 * 전체 학습 현황을 분석하는 함수
 * @param {Array} data - 파싱된 학습로그 데이터
 * @returns {Object} 전체 현황 분석 결과
 */
export function analyzeOverallStats(data) {
  if (!data || data.length === 0) {
    return {
      totalStudents: 0,
      totalLearningTime: 0,
      averageAccuracy: 0,
      activityStats: [],
      dailyStats: [],
      functionStats: []
    };
  }

  // 기본 통계
  const uniqueStudents = new Set(data.map(entry => entry['학생이름'])).size;
  const totalLearningTime = data.reduce((sum, entry) => sum + (entry.learningTimeInSeconds || 0), 0);
  
  // 평균 정답률 계산
  const validEntries = data.filter(entry => entry.parsedScore && entry.parsedScore.total > 0);
  const averageAccuracy = validEntries.length > 0 
    ? Math.round(validEntries.reduce((sum, entry) => sum + entry.parsedScore.accuracy, 0) / validEntries.length)
    : 0;

  // 기능별 통계
  const functionStats = {};
  data.forEach(entry => {
    const funcName = entry['기능명'];
    if (!functionStats[funcName]) {
      functionStats[funcName] = { count: 0, totalAccuracy: 0, validCount: 0 };
    }
    functionStats[funcName].count++;
    
    if (entry.parsedScore && entry.parsedScore.total > 0) {
      functionStats[funcName].totalAccuracy += entry.parsedScore.accuracy;
      functionStats[funcName].validCount++;
    }
  });

  // 기능별 평균 정답률 계산
  const functionStatsArray = Object.entries(functionStats).map(([name, stats]) => ({
    name,
    count: stats.count,
    averageAccuracy: stats.validCount > 0 ? Math.round(stats.totalAccuracy / stats.validCount) : 0
  }));

  // 일별 통계
  const dailyStats = {};
  data.forEach(entry => {
    if (entry.parsedAccessTime && !isNaN(entry.parsedAccessTime.getTime())) {
      try {
        const dateKey = entry.parsedAccessTime.toISOString().split('T')[0];
        if (!dailyStats[dateKey]) {
          dailyStats[dateKey] = { date: dateKey, count: 0, students: new Set() };
        }
        dailyStats[dateKey].count++;
        dailyStats[dateKey].students.add(entry['학생이름']);
      } catch (error) {
        console.warn('⚠️ 일별 통계 처리 중 오류:', error, entry['접속시간']);
      }
    }
  });

  const dailyStatsArray = Object.values(dailyStats).map(stat => ({
    date: stat.date,
    count: stat.count,
    uniqueStudents: stat.students.size
  }));

  return {
    totalStudents: uniqueStudents,
    totalLearningTime,
    averageAccuracy,
    functionStats: functionStatsArray.sort((a, b) => b.count - a.count),
    dailyStats: dailyStatsArray.sort((a, b) => a.date.localeCompare(b.date))
  };
}

/**
 * 특정 학생의 학습 데이터를 분석하는 함수
 * @param {Array} data - 파싱된 학습로그 데이터
 * @param {string} studentName - 분석할 학생 이름
 * @returns {Object} 학생별 분석 결과
 */
export function analyzeStudentData(data, studentName) {
  const studentData = data.filter(entry => entry['학생이름'] === studentName);
  
  if (studentData.length === 0) {
    return null;
  }

  // 기본 통계
  const totalSessions = studentData.length;
  const totalLearningTime = studentData.reduce((sum, entry) => sum + (entry.learningTimeInSeconds || 0), 0);
  
  // 평균 정답률
  const validEntries = studentData.filter(entry => entry.parsedScore && entry.parsedScore.total > 0);
  const averageAccuracy = validEntries.length > 0 
    ? Math.round(validEntries.reduce((sum, entry) => sum + entry.parsedScore.accuracy, 0) / validEntries.length)
    : 0;

  // 주로 사용하는 기능
  const functionUsage = {};
  studentData.forEach(entry => {
    const funcName = entry['기능명'];
    functionUsage[funcName] = (functionUsage[funcName] || 0) + 1;
  });
  
  const mostUsedFunction = Object.entries(functionUsage)
    .sort(([,a], [,b]) => b - a)[0]?.[0] || '';

  // 시간별 학습 추이
  const learningTrend = studentData
    .filter(entry => entry.parsedAccessTime && !isNaN(entry.parsedAccessTime.getTime()))
    .map(entry => {
      try {
        // 한국 시간 기준으로 날짜 포맷팅 (UTC 변환 없이)
        const date = entry.parsedAccessTime;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        
        console.log('📅 학습 추이 날짜 처리:', entry['접속시간'], '→', dateString);
        
        const learningTime = entry.learningTimeInSeconds || 0;
        const accuracy = entry.parsedScore?.accuracy ?? 0; // null/undefined만 0으로, 0은 유지
        
        console.log('📊 학습 데이터:', {
          date: dateString,
          learningTime,
          accuracy,
          hasScore: !!entry.parsedScore,
          originalScore: entry.parsedScore
        });
        
        return {
          date: dateString,
          originalAccessTime: entry['접속시간'], // 디버그용
          learningTime,
          accuracy
        };
      } catch (error) {
        console.warn('⚠️ 학습 추이 처리 중 오류:', error, entry['접속시간']);
        return null;
      }
    })
    .filter(item => item !== null);

  console.log('🔍 집계 전 learningTrend:', learningTrend);

  // 같은 날짜 데이터를 집계 (평균값 사용)
  const dateGroups = {};
  learningTrend.forEach(item => {
    if (!dateGroups[item.date]) {
      dateGroups[item.date] = {
        learningTimes: [],
        accuracies: [],
        originalTimes: []
      };
    }
    dateGroups[item.date].learningTimes.push(item.learningTime);
    dateGroups[item.date].accuracies.push(item.accuracy);
    dateGroups[item.date].originalTimes.push(item.originalAccessTime);
  });

  console.log('🔍 dateGroups:', dateGroups);

  const aggregatedTrend = Object.entries(dateGroups)
    .map(([date, data]) => {
      const avgLearningTime = Math.round(
        data.learningTimes.reduce((sum, time) => sum + time, 0) / data.learningTimes.length
      );
      const avgAccuracy = Math.round(
        data.accuracies.reduce((sum, acc) => sum + acc, 0) / data.accuracies.length
      );
      
      console.log(`📈 ${date} 집계:`, {
        sessions: data.learningTimes.length,
        avgLearningTime,
        avgAccuracy,
        rawLearningTimes: data.learningTimes,
        rawAccuracies: data.accuracies
      });
      
      return {
        date,
        learningTime: avgLearningTime,
        accuracy: avgAccuracy,
        sessionCount: data.learningTimes.length
      };
    })
    .sort((a, b) => a.date.localeCompare(b.date));

  console.log('🔍 최종 aggregatedTrend:', aggregatedTrend);

  // 기능별 성취도
  const functionPerformance = {};
  studentData.forEach(entry => {
    const funcName = entry['기능명'];
    if (!functionPerformance[funcName]) {
      functionPerformance[funcName] = { totalAccuracy: 0, count: 0 };
    }
    
    if (entry.parsedScore && entry.parsedScore.total > 0) {
      functionPerformance[funcName].totalAccuracy += entry.parsedScore.accuracy;
      functionPerformance[funcName].count++;
    }
  });

  const functionPerformanceArray = Object.entries(functionPerformance)
    .filter(([, stats]) => stats.count > 0)
    .map(([name, stats]) => {
      const averageAccuracy = Math.round(stats.totalAccuracy / stats.count);
      const functionDisplayName = {
        'short-answer-quiz': '주관식 퀴즈',
        'multiple-choice-quiz': '객관식 퀴즈', 
        'flashcard-template': '단어 카드',
        'question-generation-quiz': '독해 문제',
        '문항생성': '문항 생성',
        'listening-quiz': '듣기 퀴즈'
      }[name] || name;
      
      console.log(`📊 기능별 성취도 - ${functionDisplayName}: ${averageAccuracy}% (${stats.count}회)`);
      return {
        function: functionDisplayName,
        averageAccuracy: Number(averageAccuracy),
        sessionCount: stats.count,
        originalName: name
      };
    })
    .sort((a, b) => b.averageAccuracy - a.averageAccuracy); // 정답률 높은 순으로 정렬

  return {
    studentName,
    totalSessions,
    totalLearningTime,
    averageAccuracy,
    mostUsedFunction,
    learningTrend: aggregatedTrend,
    functionPerformance: functionPerformanceArray,
    activities: studentData.sort((a, b) => 
      new Date(b['접속시간']) - new Date(a['접속시간'])
    )
  };
}

/**
 * 특정 활동의 상세 분석
 * @param {Object} activityEntry - 분석할 활동 데이터
 * @returns {Object} 활동 상세 분석 결과
 */
export function analyzeActivityDetail(activityEntry) {
  if (!activityEntry || !activityEntry.parsedMetadata) {
    return null;
  }

  const metadata = activityEntry.parsedMetadata;
  const funcName = activityEntry['기능명'];

  // 공통 정보
  const baseInfo = {
    activityType: funcName,
    studentName: activityEntry['학생이름'],
    accessTime: activityEntry['접속시간'],
    learningTime: activityEntry['학습시간'],
    hintUsed: activityEntry['힌트사용여부'] === 'TRUE',
    scoreInfo: activityEntry.parsedScore
  };

  // 기능별 상세 분석
  switch (funcName) {
    case 'short-answer-quiz':
      return {
        ...baseInfo,
        totalQuestions: metadata.totalQuestions || 0,
        hintUsedCount: metadata.hintUsedCount || 0,
        detailedAnswers: metadata.detailedAnswers ? JSON.parse(metadata.detailedAnswers) : [],
        analysisType: 'quiz'
      };

    case 'multiple-choice-quiz':
      return {
        ...baseInfo,
        totalQuestions: metadata.totalQuestions || 0,
        schoolLevel: metadata.schoolLevel || '',
        grade: metadata.grade || '',
        detailedAnswers: metadata.detailedAnswers ? JSON.parse(metadata.detailedAnswers) : [],
        analysisType: 'quiz'
      };

    case 'flashcard-template':
      return {
        ...baseInfo,
        totalWords: metadata.totalWords || 0,
        knownWordsCount: metadata.knownWordsCount || 0,
        unknownWordsCount: metadata.unknownWordsCount || 0,
        wordList: metadata.wordList || '',
        finalAccuracy: metadata.finalAccuracy || 0,
        isRetryMode: metadata.isRetryMode || false,
        analysisType: 'flashcard'
      };

    case 'question-generation-quiz':
      return {
        ...baseInfo,
        passageId: metadata.passageId || '',
        passagePreview: metadata.passagePreview || '',
        totalQuestions: metadata.totalQuestions || 0,
        questionsCount: metadata.questionsCount || 0,
        totalScore: metadata.totalScore || 0,
        maxScore: metadata.maxScore || 0,
        percentage: metadata.percentage || 0,
        totalTime: metadata.totalTime || 0,
        analysisType: 'reading'
      };

    case '쉐도잉 학습':
      return {
        ...baseInfo,
        totalSentences: metadata.finalResults?.totalSentences || 0,
        passedSentences: metadata.finalResults?.passedSentences || 0,
        failedSentences: metadata.finalResults?.failedSentences || 0,
        averageAccuracy: metadata.finalResults?.averageAccuracy || 0,
        passRate: metadata.finalResults?.passRate || 0,
        sentenceScores: metadata.finalResults?.sentenceScores || [],
        practicedSentences: metadata.practicedSentences || [],
        analysisType: 'shadowing'
      };

    default:
      return {
        ...baseInfo,
        rawMetadata: metadata,
        analysisType: 'general'
      };
  }
}